// Import Tailwind CSS
import '../StyleSheets/main.scss';

// Swiper base styles ship eagerly in <head> (via main.css) so the slider layout
// (single-slide viewport, overflow:hidden, flex row) is present from the first
// paint. Only the Swiper *library* is loaded lazily below. Without this the
// sliders render as a tall stacked column until the JS chunk resolves.
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

// Import components
import { initCardWrappers } from './components/card-wrapper.js';
import { initCollapse } from './components/collapse.js';
import { initAccordion } from './components/accordion.js';
import { initAnchorOffset } from './components/anchor-offset.js';
import { initHeaderScroll } from './components/header-scroll.js';
import { initScrollbarWidth } from './components/scrollbar-width.js';
import { initLocationMap } from './components/location-map.js';
import { initOfferFilter } from './components/offer-filter.js';
import { initSiteSearch } from './components/site-search.js';
import { initOfferBooking } from './components/offer-booking.js';
import { initOfferFavorites } from './components/offer-favorites.js';
import { initBerufsbildOffers } from './components/berufsbild-offers.js';

// Lazy-load the Swiper library when needed (its CSS is already bundled above)
let Swiper, Navigation, Pagination, Autoplay, EffectFade;
let swiperLoaded = false;

async function loadSwiper() {
  if (swiperLoaded) return;
  const swiperModule = await import('swiper');
  const modulesModule = await import('swiper/modules');
  Swiper = swiperModule.default;
  Navigation = modulesModule.Navigation;
  Pagination = modulesModule.Pagination;
  Autoplay = modulesModule.Autoplay;
  EffectFade = modulesModule.EffectFade;

  swiperLoaded = true;
}

// Optimize hero slideshow video loading for PageSpeed.
// Runs on DOMContentLoaded BEFORE loadSwiper() so the first hero video starts
// downloading and the loader overlay resolves independently of the Swiper chunk.
// The per-slide lazy-load on slideChange is wired separately, after new Swiper().
function initHeroVideoLoading() {
  const heroSlideshow = document.querySelector('.hero-slideshow-swiper');
  if (!heroSlideshow) return;

  const allVideos = heroSlideshow.querySelectorAll('video[data-video-url]');
  const loaderElement = heroSlideshow.querySelector('.hero-slideshow-loader');
  if (allVideos.length === 0) return;

  // Add video sources optimized for PageSpeed
  allVideos.forEach((video, index) => {
    const desktopUrl = video.dataset.videoUrl;
    const mobileUrl = desktopUrl.replace(/\.mp4$/i, '_mobile.mp4');

    // Add mobile source (browser will ignore if file doesn't exist)
    const mobileSource = document.createElement('source');
    mobileSource.src = mobileUrl;
    mobileSource.type = 'video/mp4';
    mobileSource.media = '(max-width: 767px)';
    video.appendChild(mobileSource);

    // Add desktop source (fallback)
    const desktopSource = document.createElement('source');
    desktopSource.src = desktopUrl;
    desktopSource.type = 'video/mp4';
    video.appendChild(desktopSource);

    // First video: high priority for LCP
    if (index === 0) {
      video.setAttribute('fetchpriority', 'high');
      video.setAttribute('preload', 'auto');
    } else {
      // Other videos: lazy load to save bandwidth
      video.setAttribute('preload', 'none');
      video.setAttribute('fetchpriority', 'low');
    }

    // Trigger video load
    video.load();
  });

  // Hide loader and show content when first video is fully buffered
  const firstVideo = allVideos[0];
  if (!loaderElement || !firstVideo) return;

  let isLoaderHidden = false;

  const hideLoader = () => {
    if (isLoaderHidden) return;
    isLoaderHidden = true;

    // Hide loader with fade-out
    loaderElement.classList.add('hide');
    setTimeout(() => loaderElement.remove(), 500);

    // Show slide content with fade-in (cross-fade effect)
    heroSlideshow.classList.add('loaded');
  };

  // Minimum loading time: 800ms (ensures smooth experience, not too fast/jumpy)
  const minLoadTime = 800;
  const startTime = Date.now();

  // Maximum wait time: 8 seconds (for very slow connections)
  const maxTimeout = setTimeout(() => {
    console.warn('Hero video loading timeout - showing content anyway');
    hideLoader();
  }, 8000);

  const onVideoReady = () => {
    clearTimeout(maxTimeout);

    // Ensure minimum loading time has passed (prevents jarring quick flash)
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, minLoadTime - elapsed);

    setTimeout(hideLoader, remaining);
  };

  // Wait for 'canplaythrough' - video has buffered enough to play without stalling
  // This is more reliable than 'canplay' for smooth playback
  firstVideo.addEventListener('canplaythrough', onVideoReady, { once: true });

  // Also hide on error (don't block forever)
  firstVideo.addEventListener('error', () => {
    clearTimeout(maxTimeout);
    console.error('Hero video loading error');
    hideLoader();
  }, { once: true });

  // If video is already ready (cached), trigger immediately
  if (firstVideo.readyState >= 3) { // HAVE_FUTURE_DATA or better
    onVideoReady();
  }
}

// Add your custom JavaScript here
console.log('BFW Sitepackage loaded');
console.log('BFW Sitepackage loaded again');

// Swiper-Breakpoints an die Container-Stufen lg/2xl angeglichen (siehe
// tailwind.config.js), statt je Slider-Instanz eigene, voneinander abweichende
// Zahlenwerte zu pflegen. Entsprechen den im Markup verwendeten Tailwind-Variants
// "slider-lg"/"slider-2xl" (Pfeile, Innenabstand).
const BREAKPOINT_LG = 960;
const BREAKPOINT_2XL = 1400;

document.addEventListener('DOMContentLoaded', async () => {
  console.log('DOM fully loaded');

  // Suchfelder in Header/Hero (Partials/ContentElements/SearchBox.html)
  initSiteSearch();

  // Höhe des sticky Headers als Scroll-Offset für Anker-Sprungziele
  initAnchorOffset();

  // Graue Unterlinie am sticky Header im gescrollten Zustand
  initHeaderScroll();

  // Scrollbar-Breite für .full-width-breakout (100vw ohne Scrollleiste)
  initScrollbarWidth();

  // Initialize card wrappers (make entire cards clickable)
  initCardWrappers();

  // Weiches Auf-/Zuklappen der Accordions (<details>)
  initCollapse();

  // Accordion: nur ein Eintrag gleichzeitig offen
  initAccordion();

  // Start hero video download + loader BEFORE the Swiper library is fetched,
  // so hero preloading no longer waits on the Swiper chunk.
  initHeroVideoLoading();

  // Load Swiper only if any slider exists on the page
  const hasSliders = document.querySelector('.news-list-slider-instance, .content-slider-instance, .review-slider-instance, .impression-slider-instance, .hero-slideshow-swiper, .static-hero-swiper');
  if (hasSliders) {
    await loadSwiper();
  }

  // Slider-Einstellungen der Teaser-Slider (tt_content-Palette bfwTeaserSlider), von den
  // Templates als data-speed/data-autoplay/data-delay am .swiper-Element ausgegeben.
  // Autoplay nur bei data-autoplay="1", sonst wird nur über Pfeile/Pagination geblättert.
  const teaserSliderOptions = (swiperEl) => ({
    speed: parseInt(swiperEl.dataset.speed) || 300,
    ...(swiperEl.dataset.autoplay === '1' ? {
      autoplay: {
        delay: parseInt(swiperEl.dataset.delay) || 5000,
        disableOnInteraction: false,
      },
    } : {}),
  });

  // Initialize Swiper for News List Sliders (tx_news templateLayout=Slider, gilt für
  // Standard News und Blog-Teaser). Mehrere Instanzen können gleichzeitig auf
  // derselben Seite stehen, daher werden Navigation/Pagination pro Instanz gescoped
  // statt über global eindeutige Selektoren angesprochen.
  document.querySelectorAll('.news-list-slider-instance').forEach((instance) => {
    const swiperEl = instance.querySelector('.news-list-swiper');
    if (!swiperEl) return;

    new Swiper(swiperEl, {
      modules: [Navigation, Pagination, Autoplay],
      slidesPerView: 1,
      spaceBetween: 24,
      loop: true,
      ...teaserSliderOptions(swiperEl),
      pagination: {
        el: swiperEl.querySelector('.swiper-pagination-news-list'),
        clickable: true,
        bulletClass: 'news-slide-bullet',
        bulletActiveClass: 'news-slide-bullet-active',
        renderBullet: (index, className) => `<button class="${className}" aria-label="Slide ${index + 1}"></button>`,
      },
      navigation: {
        nextEl: instance.querySelector('.swiper-button-next-news-list'),
        prevEl: instance.querySelector('.swiper-button-prev-news-list'),
      },
      breakpoints: {
        [BREAKPOINT_LG]: {
          slidesPerView: 2,
        },
        [BREAKPOINT_2XL]: {
          slidesPerView: 3,
        },
      },
    });
  });

  // Initialize Swiper for Content Slider (bfw_content_slider Content Element,
  // "Aktuelle Angebote"-Style Karten-Slider). Mehrere Instanzen können gleichzeitig
  // auf derselben Seite stehen, daher werden Navigation/Pagination pro Instanz
  // gescoped statt über global eindeutige Selektoren angesprochen. slidesPerGroup
  // entspricht jeweils slidesPerView, damit die Pagination-Dots Kartengruppen statt
  // einzelner Slides abbilden.
  document.querySelectorAll('.content-slider-instance').forEach((instance) => {
    const swiperEl = instance.querySelector('.content-slider-swiper');
    if (!swiperEl) return;

    new Swiper(swiperEl, {
      modules: [Navigation, Pagination, Autoplay],
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 24,
      loop: true,
      ...teaserSliderOptions(swiperEl),
      pagination: {
        el: instance.querySelector('.swiper-pagination-content-slider'),
        clickable: true,
        bulletClass: 'content-slide-bullet',
        bulletActiveClass: 'content-slide-bullet-active',
        renderBullet: (index, className) => `<button class="${className}" aria-label="Slide ${index + 1}"></button>`,
      },
      navigation: {
        nextEl: instance.querySelector('.swiper-button-next-content-slider'),
        prevEl: instance.querySelector('.swiper-button-prev-content-slider'),
      },
      breakpoints: {
        [BREAKPOINT_LG]: {
          slidesPerView: 2,
          slidesPerGroup: 2,
        },
        [BREAKPOINT_2XL]: {
          slidesPerView: 3,
          slidesPerGroup: 3,
        },
      },
    });
  });

  // Initialize Swiper for Kundenbewertungen-Slider (Plugin CustomerReviews,
  // Ansicht ohne Formular). Mehrere Plugin-Instanzen können gleichzeitig auf
  // derselben Seite stehen, daher werden Navigation/Pagination pro Instanz gescoped
  // statt über global eindeutige Selektoren angesprochen.
  document.querySelectorAll('.review-slider-instance').forEach((instance) => {
    const swiperEl = instance.querySelector('.review-swiper');
    if (!swiperEl) return;

    // data-max-slides (Partial Review/Slider, Argument maxSlides) begrenzt die gleichzeitig
    // sichtbaren Slides, z.B. 2 beim ProvenExpert-Element
    const maxSlides = parseInt(swiperEl.dataset.maxSlides) || 3;
    const slidesLg = Math.min(2, maxSlides);
    const slides2xl = Math.min(3, maxSlides);
    // data-space-between (Argument spaceBetween): Abstand zwischen den Slides in px
    const spaceBetween = parseInt(swiperEl.dataset.spaceBetween) || 24;

    new Swiper(swiperEl, {
      modules: [Navigation, Pagination, Autoplay],
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween,
      loop: true,
      ...teaserSliderOptions(swiperEl),
      pagination: {
        el: instance.querySelector('.swiper-pagination-review'),
        clickable: true,
        bulletClass: 'review-slide-bullet',
        bulletActiveClass: 'review-slide-bullet-active',
        renderBullet: (index, className) => `<button class="${className}" aria-label="Slide ${index + 1}"></button>`,
      },
      navigation: {
        nextEl: instance.querySelector('.swiper-button-next-review'),
        prevEl: instance.querySelector('.swiper-button-prev-review'),
      },
      breakpoints: {
        [BREAKPOINT_LG]: {
          slidesPerView: slidesLg,
          slidesPerGroup: slidesLg,
        },
        [BREAKPOINT_2XL]: {
          slidesPerView: slides2xl,
          slidesPerGroup: slides2xl,
        },
      },
    });
  });

  // Impressionen-Slider der Standort-Detailansicht (Templates/Location/Show.html):
  // Bildreihe mit Pfeilen, ohne Autoplay/Pagination. Navigation pro Instanz gescoped.
  document.querySelectorAll('.impression-slider-instance').forEach((instance) => {
    const swiperEl = instance.querySelector('.impression-swiper');
    if (!swiperEl) return;

    new Swiper(swiperEl, {
      modules: [Navigation],
      slidesPerView: 1.2,
      spaceBetween: 16,
      loop: true,
      navigation: {
        nextEl: instance.querySelector('.swiper-button-next-impression'),
        prevEl: instance.querySelector('.swiper-button-prev-impression'),
      },
      breakpoints: {
        [BREAKPOINT_LG]: {
          slidesPerView: 3,
          spaceBetween: 24,
        },
        [BREAKPOINT_2XL]: {
          slidesPerView: 4,
          spaceBetween: 24,
        },
      },
    });
  });

  // Hero Slideshow via Swiper (bfw_hero_slideshow Content Element)
  document.querySelectorAll('.hero-slideshow-swiper').forEach(el => {
    const speed = parseInt(el.dataset.speed) || 300;
    const delay = parseInt(el.dataset.delay) || 5000;
    const effect = el.dataset.effect === 'fade' ? 'fade' : 'slide';

    const modules = [Navigation, Pagination, Autoplay];
    if (effect === 'fade') modules.push(EffectFade);

    new Swiper(el, {
      modules,
      loop: true,
      speed,
      effect,
      ...(effect === 'fade' ? { fadeEffect: { crossFade: true } } : {}),
      autoplay: {
        delay,
        disableOnInteraction: false,
      },
      pagination: {
        el: el.querySelector('.hero-slideshow-pagination'),
        clickable: true,
        bulletClass: 'hero-slide-bullet',
        bulletActiveClass: 'hero-slide-bullet-active',
        renderBullet: (index, className) => `<button class="${className}" aria-label="Slide ${index + 1}"></button>`,
      },
    });

    // Lazy-load the remaining hero videos once the user advances a slide.
    // Source injection + first-video preload already happened in initHeroVideoLoading().
    if (el.querySelectorAll('video[data-video-url]').length > 1 && el.swiper) {
      el.swiper.on('slideChange', () => {
        const activeSlide = el.swiper.slides[el.swiper.activeIndex];
        const activeVideo = activeSlide?.querySelector('video');

        if (activeVideo && activeVideo.getAttribute('preload') === 'none') {
          activeVideo.setAttribute('preload', 'auto');
          activeVideo.load();
        }
      });
    }
  });

  // Bild-Slider im Header/Hero (bfw_static_hero Content Element), nur aktiv wenn
  // mehr als 1 Bild im "assets"-Feld liegt (siehe BfwStaticHero.html)
  document.querySelectorAll('.static-hero-swiper').forEach(el => {
    new Swiper(el, {
      modules: [Pagination, Autoplay],
      loop: true,
      speed: 500,
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
      },
      pagination: {
        el: el.querySelector('.static-hero-pagination'),
        clickable: true,
        bulletClass: 'static-hero-slide-bullet',
        bulletActiveClass: 'static-hero-slide-bullet-active',
        renderBullet: (index, className) => `<button class="${className}" aria-label="Slide ${index + 1}"></button>`,
      },
    });
  });

  // Alphabet Navigation Filter (Themen-Teaser-Übersicht)
  const alphabetNav = document.querySelector('[data-alphabet-nav]');
  if (alphabetNav) {
    const itemsContainer = document.querySelector('[data-items-container]');
    const items = itemsContainer ? itemsContainer.querySelectorAll('[data-item]') : [];
    const letterButtons = alphabetNav.querySelectorAll('[data-letter]');
    const emptyMessage = document.querySelector('[data-empty-message]');
    const emptyReset = document.querySelector('[data-empty-reset]');

    letterButtons.forEach(button => {
      button.addEventListener('click', () => {
        const selectedLetter = button.dataset.letter;
        let visibleCount = 0;

        // Active state auf Buttons
        letterButtons.forEach(btn => btn.classList.remove('az-register__letter--active'));
        button.classList.add('az-register__letter--active');

        // Items filtern
        items.forEach(item => {
          const itemLetter = item.dataset.itemLetter;

          if (selectedLetter === 'all' || itemLetter === selectedLetter) {
            item.style.display = '';
            visibleCount++;
          } else {
            item.style.display = 'none';
          }
        });

        // Hinweis, wenn unter dem Buchstaben kein Treffer existiert
        if (emptyMessage) {
          emptyMessage.hidden = visibleCount > 0;
        }
      });
    });

    // "hier" im Hinweis setzt den Filter zurück
    if (emptyReset) {
      emptyReset.addEventListener('click', () => {
        alphabetNav.querySelector('[data-letter="all"]').click();
      });
    }
  }

  // Hauptnavigation (Partials/Navigation/Main.html): Punkte mit Unterpunkten sind Buttons.
  // Hover und Tastaturfokus öffnen das Dropdown per CSS (_navigation.scss), Klick/Tap
  // setzt zusätzlich "is-open", damit es auch auf Touch-Geräten aufgeht. Maus raus,
  // Fokus weg und Klick außerhalb schließen, Esc schließt auch bei Hover/Fokus.
  const mainNavItems = document.querySelectorAll('[data-main-nav-item]');

  if (mainNavItems.length) {
    const setMainNavOpen = (item, open) => {
      item.classList.toggle('is-open', open);
      item.querySelector('[data-main-nav-toggle]').setAttribute('aria-expanded', open ? 'true' : 'false');
    };

    const closeMainNavItem = (item) => {
      setMainNavOpen(item, false);
      item.classList.remove('is-dismissed');
    };

    mainNavItems.forEach((item) => {
      item.querySelector('[data-main-nav-toggle]').addEventListener('click', () => {
        const open = !item.classList.contains('is-open');
        mainNavItems.forEach(closeMainNavItem);
        setMainNavOpen(item, open);
      });

      item.addEventListener('mouseleave', () => closeMainNavItem(item));

      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) closeMainNavItem(item);
      });
    });

    document.addEventListener('click', (event) => {
      mainNavItems.forEach((item) => {
        if (!item.contains(event.target)) closeMainNavItem(item);
      });
    });

    // Fokus aus dem Dropdown zurück auf den Button, sonst steht er auf einem unsichtbaren Link
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      mainNavItems.forEach((item) => {
        if (!item.matches(':hover, .is-open') && !item.contains(document.activeElement)) return;
        if (item.contains(document.activeElement)) item.querySelector('[data-main-nav-toggle]').focus();
        setMainNavOpen(item, false);
        item.classList.add('is-dismissed');
      });
    });
  }

  // Mobile menu toggle
  const mobileMenuToggle = document.getElementById('mobile-menu-toggle');
  const mobileMenu = document.getElementById('mobile-menu');

  if (mobileMenuToggle && mobileMenu) {
    const closeMobileMenu = () => {
      mobileMenu.classList.add('hidden');
      mobileMenuToggle.setAttribute('aria-expanded', 'false');
      // Akkordeon zurück auf den Ausgangszustand: nur der aktive Bereich offen
      mobileMenu.querySelectorAll('details[name="mobile-nav"]').forEach((details) => {
        details.open = details.hasAttribute('data-mobile-nav-active');
      });
    };

    const openMobileMenu = () => {
      mobileMenu.classList.remove('hidden');
      mobileMenuToggle.setAttribute('aria-expanded', 'true');
    };

    // Geöffneten Hauptpunkt oben ins Menü scrollen: #mobile-menu ist der Scrollcontainer
    // (Header auf max-h-dvh begrenzt), und das Schließen des vorher offenen Punkts
    // (exklusives Akkordeon) verschiebt ihn sonst aus dem Blick
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    mobileMenu.querySelectorAll('details[name="mobile-nav"]').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open || mobileMenu.classList.contains('hidden')) return;
        const offset = details.getBoundingClientRect().top - mobileMenu.getBoundingClientRect().top;
        mobileMenu.scrollTo({
          top: mobileMenu.scrollTop + offset,
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
        });
      });
    });

    mobileMenuToggle.addEventListener('click', () => {
      if (mobileMenu.classList.contains('hidden')) {
        openMobileMenu();
      } else {
        closeMobileMenu();
      }
    });

    // Klick außerhalb schließt das Menü
    document.addEventListener('click', (event) => {
      if (mobileMenu.classList.contains('hidden')) return;
      if (mobileMenu.contains(event.target) || mobileMenuToggle.contains(event.target)) return;
      closeMobileMenu();
    });

    // Escape schließt das Menü und gibt den Fokus zurück an den Toggle-Button
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !mobileMenu.classList.contains('hidden')) {
        closeMobileMenu();
        mobileMenuToggle.focus();
      }
    });
  }

  // Scroll-to-top button
  const scrollToTopButton = document.getElementById('scroll-to-top');
  if (scrollToTopButton) {
    const SCROLL_THRESHOLD = 300;

    const toggleVisibility = () => {
      scrollToTopButton.classList.toggle('is-visible', window.scrollY > SCROLL_THRESHOLD);
    };

    toggleVisibility();
    window.addEventListener('scroll', toggleVisibility, { passive: true });

    scrollToTopButton.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // Kursübersicht Filter (Partials/Kurs/Filter.html). Rein UI-seitig: Dropdown-Zustand,
  // Chips, Entfernungs-Anzeige, Mobile-Overlay - filtert noch NICHT die eigentliche
  // Kursliste, dafür fehlt aktuell eine Datenanbindung (siehe Kommentar im Partial).
  const kursFilter = document.querySelector('[data-kurs-filter]');
  if (kursFilter) {
    const panel = kursFilter.querySelector('#kurs-filter-panel');
    const trigger = kursFilter.querySelector('[data-kurs-filter-trigger]');
    const closeButtons = kursFilter.querySelectorAll('[data-kurs-filter-close]');
    const chipsRow = kursFilter.querySelector('[data-kurs-chips-row]');
    const chipsContainer = kursFilter.querySelector('[data-kurs-chips]');
    const resetButton = kursFilter.querySelector('[data-kurs-filter-reset]');
    const checkboxes = kursFilter.querySelectorAll('[data-filter-checkbox]');
    const filterTotalBadge = kursFilter.querySelector('[data-kurs-filter-count]');
    const distanceRange = kursFilter.querySelector('[data-kurs-distance-range]');
    const distanceOutput = kursFilter.querySelector('[data-kurs-distance-output]');
    const dropdowns = kursFilter.querySelectorAll('[data-filter-group-details]');

    // Mobile-Overlay öffnen/schließen, gleiches hidden/aria-expanded-Prinzip wie
    // #mobile-menu-toggle oben
    if (panel && trigger) {
      const openPanel = () => {
        panel.classList.remove('hidden');
        trigger.setAttribute('aria-expanded', 'true');
      };
      const closePanel = () => {
        panel.classList.add('hidden');
        trigger.setAttribute('aria-expanded', 'false');
      };

      trigger.addEventListener('click', openPanel);
      closeButtons.forEach(button => button.addEventListener('click', closePanel));

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !panel.classList.contains('hidden')) {
          closePanel();
          trigger.focus();
        }
      });
    }

    const updateGroupCount = (groupKey) => {
      const details = kursFilter.querySelector(`[data-filter-group-details][data-filter-group="${groupKey}"]`);
      const badge = details ? details.querySelector('[data-filter-group-count]') : null;
      if (!badge) return;
      const count = kursFilter.querySelectorAll(`[data-filter-checkbox][data-filter-group="${groupKey}"]:checked`).length;
      badge.textContent = count;
      badge.classList.toggle('hidden', count === 0);
    };

    const updateTotalBadgeAndChipsRow = () => {
      const count = chipsContainer ? chipsContainer.children.length : 0;
      if (filterTotalBadge) {
        filterTotalBadge.textContent = count;
        filterTotalBadge.classList.toggle('hidden', count === 0);
      }
      if (chipsRow) {
        chipsRow.classList.toggle('hidden', count === 0);
      }
    };

    const createChip = (checkbox) => {
      const chip = document.createElement('span');
      chip.className = 'inline-flex items-center gap-2 rounded-pill border border-gray-300 pl-3 pr-2 py-1 text-sm text-bfw-blue-gray-02';
      chip.setAttribute('data-kurs-chip', '');
      chip.dataset.filterGroup = checkbox.dataset.filterGroup;
      chip.dataset.filterValue = checkbox.dataset.filterValue;
      chip.innerHTML = `${checkbox.dataset.filterLabel}
        <button type="button" class="text-bfw-gray-500 hover:text-bfw-blue-primary" data-kurs-chip-remove aria-label="Filter „${checkbox.dataset.filterLabel}“ entfernen">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>`;
      return chip;
    };

    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        const { filterGroup, filterValue } = checkbox.dataset;

        if (checkbox.checked) {
          const exists = chipsContainer && chipsContainer.querySelector(`[data-kurs-chip][data-filter-group="${filterGroup}"][data-filter-value="${filterValue}"]`);
          if (chipsContainer && !exists) {
            chipsContainer.appendChild(createChip(checkbox));
          }
        } else if (chipsContainer) {
          const chip = chipsContainer.querySelector(`[data-kurs-chip][data-filter-group="${filterGroup}"][data-filter-value="${filterValue}"]`);
          if (chip) chip.remove();
        }

        updateGroupCount(filterGroup);
        updateTotalBadgeAndChipsRow();
      });
    });

    if (chipsContainer) {
      chipsContainer.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-kurs-chip-remove]');
        if (!removeButton) return;

        const chip = removeButton.closest('[data-kurs-chip]');
        const { filterGroup, filterValue } = chip.dataset;
        const checkbox = kursFilter.querySelector(`[data-filter-checkbox][data-filter-group="${filterGroup}"][data-filter-value="${filterValue}"]`);
        if (checkbox) checkbox.checked = false;

        chip.remove();
        updateGroupCount(filterGroup);
        updateTotalBadgeAndChipsRow();
      });
    }

    if (resetButton) {
      resetButton.addEventListener('click', () => {
        checkboxes.forEach((checkbox) => { checkbox.checked = false; });
        if (chipsContainer) chipsContainer.innerHTML = '';
        dropdowns.forEach((details) => {
          details.removeAttribute('open');
          updateGroupCount(details.dataset.filterGroup);
        });
        updateTotalBadgeAndChipsRow();

        if (distanceRange && distanceOutput) {
          distanceRange.value = distanceRange.dataset.defaultValue;
          distanceOutput.textContent = distanceRange.value;
        }
      });
    }

    // Entfernungs-Wert live aktualisieren
    if (distanceRange && distanceOutput) {
      distanceRange.addEventListener('input', () => {
        distanceOutput.textContent = distanceRange.value;
      });
    }

    // Nur ein Filter-Dropdown gleichzeitig offen (bessere Übersicht bei vielen Filtern)
    dropdowns.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        dropdowns.forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
      });
    });

    // Klick außerhalb schließt offene Filter-Dropdowns
    document.addEventListener('click', (event) => {
      dropdowns.forEach((details) => {
        if (details.open && !details.contains(event.target)) {
          details.removeAttribute('open');
        }
      });
    });
  }

  // Bildungsstätten-Umkreissuche (Templates/Location/List.html): Entfernungswert live anzeigen
  const locationRadius = document.querySelector('[data-location-search] [data-location-radius]');
  const locationRadiusOutput = document.querySelector('[data-location-search] [data-location-radius-output]');
  if (locationRadius && locationRadiusOutput) {
    locationRadius.addEventListener('input', () => {
      locationRadiusOutput.textContent = locationRadius.value;
    });
  }

  initLocationMap();

  // PIM-Angebotsliste: Live-Filter (Templates/Offer/List.html)
  initOfferFilter();

  // Buchungsformular: Ein-/Ausblenden, Personenblöcke (Templates/OfferBooking/Form.html)
  initOfferBooking();

  // Merkliste: Herz-Buttons, Zähler im Header, Seite "Ihre Favoriten" (Development/offer-favorites.md)
  initOfferFavorites();

  // Berufsbild-Detailseite: "Mehr zeigen" unter den Angebotskarten (Development/berufsbild-offers.md)
  initBerufsbildOffers();
});

